/**
 * A notification's presentation (F3-C): i18n title + deep-link destination
 * by kind. The payload is a synced JSON string (scalar column).
 */
import { msg } from "@lingui/core/macro";
import type { MessageDescriptor } from "@lingui/core";
import { isPlainObject } from "@space/shared";
import type { Notification } from "@space/powersync";

export interface NotificationView {
  title: MessageDescriptor;
  /** Relative path (u-router) to navigate to on tap. */
  to: string;
}

function parsePayload(raw: string | null | undefined): Record<string, unknown> {
  if (!raw) {
    return {};
  }
  try {
    const v: unknown = JSON.parse(raw);
    return isPlainObject(v) ? v : {};
  } catch {
    return {};
  }
}

/** Resolves a notification's title + deep-link based on its kind. */
export function notificationView(n: Notification): NotificationView {
  const payload = parsePayload(n.payload);
  switch (n.kind ?? "") {
    case "announcement": {
      return { title: msg`New announcement`, to: "/academy/announcements" };
    }
    case "forum_reply": {
      return { title: msg`New reply in a forum thread`, to: "/learn" };
    }
    case "enrollment_status": {
      const status = typeof payload.status === "string" ? payload.status : "";
      if (status === "active") {
        return { title: msg`You are now enrolled`, to: "/learn" };
      }
      if (status === "waitlisted") {
        return { title: msg`You are on the waitlist`, to: "/learn" };
      }
      return { title: msg`Your enrollment status changed`, to: "/learn" };
    }
    case "grade_posted": {
      return { title: msg`A new grade was posted`, to: "/learn" };
    }
    case "submission_reviewed": {
      const outcome = typeof payload.outcome === "string" ? payload.outcome : "";
      if (outcome === "returned") {
        return { title: msg`Your submission was returned for changes`, to: "/assignments" };
      }
      if (outcome === "approved") {
        return { title: msg`Your submission was approved`, to: "/assignments" };
      }
      return { title: msg`Your submission was reviewed`, to: "/assignments" };
    }
    case "attendance_alert": {
      return { title: msg`Attendance alert`, to: "/learn" };
    }
    case "due_soon": {
      return { title: msg`Something is due soon`, to: "/learn" };
    }
    // F4-D (money): all three kinds land on "Mis pagos", which is where the
    // student sees the debt and records the payment.
    case "payment_due": {
      const stage = typeof payload.stage === "string" ? payload.stage : "";
      if (stage === "due_today") {
        return { title: msg`Your instalment is due today`, to: "/payments" };
      }
      return { title: msg`Your instalment is due soon`, to: "/payments" };
    }
    case "payment_overdue": {
      return { title: msg`Your instalment is overdue`, to: "/payments" };
    }
    case "payment_received": {
      return { title: msg`We received your payment`, to: "/payments" };
    }
    // F4-D: the class recording is already published.
    case "recording_ready": {
      return { title: msg`The class recording is ready`, to: "/schedule" };
    }
    default: {
      return { title: msg`Notification`, to: "/notifications" };
    }
  }
}
